// src/components/admin/AdminBroadcastPage.tsx
// Admin Broadcast Management — compose broadcasts, manage welcome email, view history

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import BackgroundShapes from '../common/BackgroundShapes';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// ─── TypeScript interfaces ────────────────────────────────────────────────────

interface ComponentConfig {
  question?: string;
  options?: { value: string; label: string }[];
  label?: string;
  url?: string;
  src?: string;
  alt?: string;
  link?: string;
  width?: string;
  heading?: string;
  body?: string;
}

interface EmailComponent {
  type: 'feedback' | 'cta_button' | 'image' | 'text_block';
  config: ComponentConfig;
}

interface FeedbackCount {
  value: string;
  label: string;
  count: number;
  pct: number | null;
}

interface Broadcast {
  _id: string;
  title: string;
  content: string;
  recipientFilter: 'all_signup' | 'onboarded_only';
  components: EmailComponent[];
  status: 'draft' | 'sending' | 'sent' | 'failed';
  sentCount: number;
  failedCount: number;
  sentAt: string | null;
  createdAt: string;
  feedbackBreakdown: FeedbackCount[];
  totalFeedback: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' };
}

function statusBadge(status: Broadcast['status']): React.ReactElement {
  const map: Record<Broadcast['status'], { cls: string; label: string }> = {
    draft:   { cls: 'bg-gray-100 text-gray-600 border-gray-300', label: 'Draft' },
    sending: { cls: 'bg-amber-100 text-amber-700 border-amber-300 animate-pulse', label: 'Sending…' },
    sent:    { cls: 'bg-green-100 text-green-700 border-green-300', label: 'Sent' },
    failed:  { cls: 'bg-red-100 text-red-700 border-red-300', label: 'Failed' },
  };
  const { cls, label } = map[status] || map.draft;
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded border ${cls}`}>
      {label}
    </span>
  );
}

// ─── Component builder sub-components ────────────────────────────────────────

interface FeedbackEditorProps {
  config: ComponentConfig;
  onChange: (config: ComponentConfig) => void;
}

const FeedbackEditor: React.FC<FeedbackEditorProps> = ({ config, onChange }) => {
  const options = config.options || [];

  const setQuestion = (q: string) => onChange({ ...config, question: q });

  const setOption = (idx: number, field: 'value' | 'label', val: string) => {
    const next = options.map((o, i) => i === idx ? { ...o, [field]: val } : o);
    onChange({ ...config, options: next });
  };

  const addOption = () => onChange({ ...config, options: [...options, { value: '', label: '' }] });

  const removeOption = (idx: number) => onChange({ ...config, options: options.filter((_, i) => i !== idx) });

  const applyPreset = (preset: 'thumbs' | 'helpful') => {
    if (preset === 'thumbs') {
      onChange({
        ...config,
        question: config.question || 'What do you think?',
        options: [{ value: 'up', label: '👍 Thumbs Up' }, { value: 'down', label: '👎 Thumbs Down' }],
      });
    } else {
      onChange({
        ...config,
        question: config.question || 'Was this helpful?',
        options: [{ value: 'helpful', label: 'Helpful' }, { value: 'unsubscribe', label: 'Unsubscribe' }],
      });
    }
  };

  return (
    <div className="mt-3 space-y-3">
      <div>
        <label className="block text-xs font-semibold text-coffee mb-1">Question</label>
        <input
          type="text"
          value={config.question || ''}
          onChange={e => setQuestion(e.target.value)}
          placeholder="What do you think?"
          className="w-full border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
        />
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => applyPreset('thumbs')}
          className="text-xs px-3 py-1 bg-cream border-2 border-coffee rounded-lg hover:bg-terracotta hover:text-white transition-colors font-medium"
        >
          👍/👎 Thumbs
        </button>
        <button
          type="button"
          onClick={() => applyPreset('helpful')}
          className="text-xs px-3 py-1 bg-cream border-2 border-coffee rounded-lg hover:bg-terracotta hover:text-white transition-colors font-medium"
        >
          Helpful / Unsubscribe
        </button>
      </div>

      {options.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-coffee">Options</p>
          {options.map((opt, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input
                type="text"
                value={opt.value}
                onChange={e => setOption(idx, 'value', e.target.value)}
                placeholder="value (e.g. up)"
                className="flex-1 border border-coffee/40 rounded-lg px-2 py-1 text-xs text-coffee bg-paper focus:outline-none focus:border-terracotta"
              />
              <input
                type="text"
                value={opt.label}
                onChange={e => setOption(idx, 'label', e.target.value)}
                placeholder="label (e.g. 👍 Yes)"
                className="flex-1 border border-coffee/40 rounded-lg px-2 py-1 text-xs text-coffee bg-paper focus:outline-none focus:border-terracotta"
              />
              <button
                type="button"
                onClick={() => removeOption(idx)}
                className="text-xs text-red-500 hover:text-red-700 font-bold px-1"
                aria-label="Remove option"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={addOption}
        className="text-xs text-terracotta hover:underline font-medium"
      >
        + Add option
      </button>
    </div>
  );
};

interface CtaEditorProps {
  config: ComponentConfig;
  onChange: (config: ComponentConfig) => void;
}

const CtaEditor: React.FC<CtaEditorProps> = ({ config, onChange }) => (
  <div className="mt-3 space-y-2">
    <div>
      <label className="block text-xs font-semibold text-coffee mb-1">Button label</label>
      <input
        type="text"
        value={config.label || ''}
        onChange={e => onChange({ ...config, label: e.target.value })}
        placeholder="Learn More"
        className="w-full border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-coffee mb-1">URL</label>
      <input
        type="url"
        value={config.url || ''}
        onChange={e => onChange({ ...config, url: e.target.value })}
        placeholder="https://shiyou.vercel.app/dashboard"
        className="w-full border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
      />
    </div>
  </div>
);

interface ImageEditorProps {
  config: ComponentConfig;
  onChange: (config: ComponentConfig) => void;
}

const ImageEditor: React.FC<ImageEditorProps> = ({ config, onChange }) => (
  <div className="mt-3 space-y-2">
    <div>
      <label className="block text-xs font-semibold text-coffee mb-1">Image URL (src)</label>
      <input
        type="url"
        value={config.src || ''}
        onChange={e => onChange({ ...config, src: e.target.value })}
        placeholder="https://res.cloudinary.com/…/image.jpg"
        className="w-full border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-coffee mb-1">Alt text</label>
      <input
        type="text"
        value={config.alt || ''}
        onChange={e => onChange({ ...config, alt: e.target.value })}
        placeholder="Image description"
        className="w-full border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-coffee mb-1">Link URL (optional)</label>
      <input
        type="url"
        value={config.link || ''}
        onChange={e => onChange({ ...config, link: e.target.value })}
        placeholder="https://shiyou.vercel.app/discover"
        className="w-full border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-coffee mb-1">Width (optional, default 100%)</label>
      <input
        type="text"
        value={config.width || ''}
        onChange={e => onChange({ ...config, width: e.target.value })}
        placeholder="100%"
        className="w-full border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
      />
    </div>
  </div>
);

interface TextBlockEditorProps {
  config: ComponentConfig;
  onChange: (config: ComponentConfig) => void;
}

const TextBlockEditor: React.FC<TextBlockEditorProps> = ({ config, onChange }) => (
  <div className="mt-3 space-y-2">
    <div>
      <label className="block text-xs font-semibold text-coffee mb-1">Heading (optional)</label>
      <input
        type="text"
        value={config.heading || ''}
        onChange={e => onChange({ ...config, heading: e.target.value })}
        placeholder="Section heading"
        className="w-full border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-coffee mb-1">Body text</label>
      <textarea
        value={config.body || ''}
        onChange={e => onChange({ ...config, body: e.target.value })}
        rows={3}
        placeholder="Additional text block content…"
        className="w-full border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta resize-y"
      />
    </div>
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const AdminBroadcastPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  // ── Compose form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [recipientFilter, setRecipientFilter] = useState<'all_signup' | 'onboarded_only' | 'single_recipient'>('all_signup');
  const [testRecipients, setTestRecipients] = useState<{ _id: string; email: string; name: string }[]>([]);
  const [recipientUserId, setRecipientUserId] = useState<string>('');
  const [components, setComponents] = useState<EmailComponent[]>([]);
  const [addComponentType, setAddComponentType] = useState<EmailComponent['type']>('cta_button');
  const [sending, setSending] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [composeSuccess, setComposeSuccess] = useState<string | null>(null);

  // ── Welcome email state
  const [welcomePending, setWelcomePending] = useState<number | null>(null);
  const [welcomeSending, setWelcomeSending] = useState(false);
  const [welcomeMsg, setWelcomeMsg] = useState<string | null>(null);
  const [welcomeTestUserId, setWelcomeTestUserId] = useState('');
  const [welcomeTestSending, setWelcomeTestSending] = useState(false);

  // ── Suggestion pair email state
  const [jobRunning, setJobRunning] = useState(false);
  const [jobResult, setJobResult] = useState<{ emailsSent: number; pairsSkipped: number; errors: any[] } | null>(null);
  const [singleSuggestionUserId, setSingleSuggestionUserId] = useState('');
  const [singleSuggestionSending, setSingleSuggestionSending] = useState(false);
  const [singleSuggestionResult, setSingleSuggestionResult] = useState<string | null>(null);

  // ── History state
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  // ── Fetch broadcast history
  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/broadcasts`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to load broadcasts');
      setBroadcasts(json.data.broadcasts);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load broadcasts';
      setHistoryError(msg);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // ── Fetch welcome pending count
  const fetchWelcomePending = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/broadcasts/welcome/pending-count`, { headers: authHeaders() });
      const json = await res.json();
      if (json.success) setWelcomePending(json.data.count);
    } catch {
      // Non-critical — silently ignore
    }
  }, []);

  // ── Initial load
  useEffect(() => {
    fetchHistory();
    fetchWelcomePending();
    fetch(`${API_BASE}/admin/broadcasts/test-recipients`, { headers: authHeaders() })
      .then(r => r.json())
      .then(json => { if (json.success) setTestRecipients(json.data.users); })
      .catch(() => {});
  }, [fetchHistory, fetchWelcomePending]);

  // ── Auto-poll every 5s while any broadcast is 'sending'
  useEffect(() => {
    const hasSending = broadcasts.some(b => b.status === 'sending');
    if (hasSending) {
      pollRef.current = setInterval(fetchHistory, 5000);
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [broadcasts, fetchHistory]);

  // ── Component builder helpers
  const addComponent = () => {
    const defaults: Record<EmailComponent['type'], ComponentConfig> = {
      feedback: { question: 'What do you think?', options: [] },
      cta_button: { label: 'Learn More', url: '' },
      image: { src: '', alt: '', link: '', width: '100%' },
      text_block: { heading: '', body: '' },
    };
    setComponents(prev => [...prev, { type: addComponentType, config: defaults[addComponentType] }]);
  };

  const updateComponent = (idx: number, config: ComponentConfig) => {
    setComponents(prev => prev.map((c, i) => i === idx ? { ...c, config } : c));
  };

  const removeComponent = (idx: number) => {
    setComponents(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Submit broadcast
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setComposeError(null);
    setComposeSuccess(null);

    if (!title.trim() || !content.trim()) {
      setComposeError('Title and content are required.');
      return;
    }

    setSending(true);
    try {
      // 1. Create draft
      const createRes = await fetch(`${API_BASE}/admin/broadcasts`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ title, content, recipientFilter, recipientUserId: recipientFilter === 'single_recipient' ? recipientUserId : undefined, components }),
      });
      const createJson = await createRes.json();
      if (!createRes.ok || !createJson.success) {
        setComposeError(createJson.message || 'Failed to create broadcast');
        return;
      }

      const broadcastId = createJson.data.broadcast._id;

      // 2. Send
      const sendRes = await fetch(`${API_BASE}/admin/broadcasts/${broadcastId}/send`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const sendJson = await sendRes.json();
      if (!sendRes.ok || !sendJson.success) {
        setComposeError(sendJson.message || 'Failed to start send');
        return;
      }

      setComposeSuccess('Broadcast sent! The emails are being delivered.');
      setTitle('');
      setContent('');
      setComponents([]);
      await fetchHistory();
    } catch {
      setComposeError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // ── Resend broadcast
  const [resending, setResending] = useState<string | null>(null);
  const handleResend = async (id: string) => {
    if (!window.confirm('Resend this broadcast to all eligible users?')) return;
    setResending(id);
    try {
      const res = await fetch(`${API_BASE}/admin/broadcasts/${id}/resend`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const json = await res.json();
      if (json.success) await fetchHistory();
    } catch {
      // silent — status will update via poll
    } finally {
      setResending(null);
    }
  };

  // ── Send welcome emails
  const handleSendWelcome = async () => {
    setWelcomeMsg(null);
    setWelcomeSending(true);
    try {
      const res = await fetch(`${API_BASE}/admin/broadcasts/welcome/send-all`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const json = await res.json();
      if (json.success) {
        setWelcomeMsg(`Welcome emails started for ${welcomePending ?? 0} user(s).`);
        await fetchWelcomePending();
      } else {
        setWelcomeMsg(json.message || 'Failed to start welcome send');
      }
    } catch {
      setWelcomeMsg('Network error. Please try again.');
    } finally {
      setWelcomeSending(false);
    }
  };

  const handleSendWelcomeToOne = async () => {
    if (!welcomeTestUserId) return;
    setWelcomeMsg(null);
    setWelcomeTestSending(true);
    try {
      const res = await fetch(`${API_BASE}/admin/broadcasts/welcome/send-one`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ userId: welcomeTestUserId }),
      });
      const json = await res.json();
      setWelcomeMsg(json.message || (json.success ? 'Sent!' : 'Failed'));
    } catch {
      setWelcomeMsg('Network error. Please try again.');
    } finally {
      setWelcomeTestSending(false);
    }
  };

  const handleRunSuggestionJob = async () => {
    setJobResult(null);
    setJobRunning(true);
    try {
      const res = await fetch(`${API_BASE}/admin/broadcasts/suggestion-pairs/run`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const json = await res.json();
      if (json.success) setJobResult(json.data);
      else setJobResult({ emailsSent: 0, pairsSkipped: 0, errors: [{ pairKey: '', message: json.message || 'Failed' }] });
    } catch {
      setJobResult({ emailsSent: 0, pairsSkipped: 0, errors: [{ pairKey: '', message: 'Network error' }] });
    } finally {
      setJobRunning(false);
    }
  };

  const handleSendSuggestionToOne = async () => {
    if (!singleSuggestionUserId) return;
    setSingleSuggestionResult(null);
    setSingleSuggestionSending(true);
    try {
      const res = await fetch(`${API_BASE}/admin/broadcasts/suggestion-pairs/send-one`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ recipientUserId: singleSuggestionUserId }),
      });
      const json = await res.json();
      setSingleSuggestionResult(json.message || (json.success ? 'Sent!' : 'Failed'));
    } catch {
      setSingleSuggestionResult('Network error. Please try again.');
    } finally {
      setSingleSuggestionSending(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-8 relative z-10 max-w-3xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-sm text-coffee opacity-60 hover:opacity-100 mb-1 block"
            >
              ← Admin Dashboard
            </button>
            <h1 className="text-3xl font-bold text-coffee">Email Broadcasts</h1>
          </div>
          <button
            onClick={handleLogout}
            className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm text-sm"
          >
            Sign Out
          </button>
        </div>

        {/* ── Section 1: Compose ─────────────────────────────────────────── */}
        <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-[4px_4px_0px_#594A42] mb-6">
          <h2 className="text-xl font-bold text-coffee mb-4">Compose Broadcast</h2>
          <form onSubmit={handleSubmit} className="space-y-4">

            <div>
              <label className="block text-sm font-semibold text-coffee mb-1">Subject / Title</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. May update from ShiYou"
                className="w-full border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-coffee mb-1">Content</label>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={5}
                placeholder="Write your email body here…"
                className="w-full border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta resize-y"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-coffee mb-1">Recipients</label>
              <div className="flex flex-wrap gap-2 items-center">
                <select
                  value={recipientFilter}
                  onChange={e => {
                    setRecipientFilter(e.target.value as 'all_signup' | 'onboarded_only' | 'single_recipient');
                    setRecipientUserId('');
                  }}
                  className="border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
                >
                  <option value="all_signup">All signup users</option>
                  <option value="onboarded_only">Onboarded users only</option>
                  <option value="single_recipient">Single recipient</option>
                </select>
                {recipientFilter === 'single_recipient' && (
                  <select
                    value={recipientUserId}
                    onChange={e => setRecipientUserId(e.target.value)}
                    className="border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
                  >
                    <option value="">— select user —</option>
                    {testRecipients.map(u => (
                      <option key={u._id} value={u._id}>{u.name} ({u.email})</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Component builder */}
            <div>
              <label className="block text-sm font-semibold text-coffee mb-2">Components</label>

              {/* Added components */}
              {components.length > 0 && (
                <div className="space-y-3 mb-3">
                  {components.map((comp, idx) => (
                    <div key={idx} className="border-2 border-coffee/30 rounded-lg p-4 bg-paper">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-coffee uppercase tracking-wide">
                          {comp.type === 'feedback' ? '📊 Feedback'
                            : comp.type === 'cta_button' ? '🔗 CTA Button'
                            : comp.type === 'image' ? '🖼️ Image'
                            : '📝 Text Block'}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeComponent(idx)}
                          className="text-xs text-red-500 hover:text-red-700 font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                      {comp.type === 'feedback' ? (
                        <FeedbackEditor config={comp.config} onChange={cfg => updateComponent(idx, cfg)} />
                      ) : comp.type === 'cta_button' ? (
                        <CtaEditor config={comp.config} onChange={cfg => updateComponent(idx, cfg)} />
                      ) : comp.type === 'image' ? (
                        <ImageEditor config={comp.config} onChange={cfg => updateComponent(idx, cfg)} />
                      ) : (
                        <TextBlockEditor config={comp.config} onChange={cfg => updateComponent(idx, cfg)} />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add component control */}
              <div className="flex items-center gap-2">
                <select
                  value={addComponentType}
                  onChange={e => setAddComponentType(e.target.value as EmailComponent['type'])}
                  className="border-2 border-coffee rounded-lg px-3 py-2 text-sm text-coffee bg-paper focus:outline-none"
                >
                  <option value="cta_button">CTA Button</option>
                  <option value="feedback">Feedback</option>
                  <option value="image">Image</option>
                  <option value="text_block">Text Block</option>
                </select>
                <button
                  type="button"
                  onClick={addComponent}
                  className="border-2 border-coffee text-coffee px-3 py-2 rounded-lg text-sm font-medium hover:bg-cream transition-colors"
                >
                  + Add Component
                </button>
              </div>
            </div>

            {/* Feedback */}
            {composeError && (
              <p className="text-sm text-red-600 font-medium">{composeError}</p>
            )}
            {composeSuccess && (
              <p className="text-sm text-green-700 font-medium">{composeSuccess}</p>
            )}

            <button
              type="submit"
              disabled={sending}
              className="bg-terracotta text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-[2px_2px_0px_#594A42] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'Sending…' : 'Send Broadcast'}
            </button>
          </form>
        </div>

        {/* ── Section 2: Welcome Email ───────────────────────────────────── */}
        <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm mb-6">
          <h2 className="text-xl font-bold text-coffee mb-2">Welcome Email</h2>
          <p className="text-coffee opacity-70 text-sm mb-4">
            Send a welcome email to all signup users who haven&rsquo;t received one yet.
          </p>
          <div className="flex items-center gap-4 flex-wrap mb-3">
            <span className="text-sm text-coffee font-medium">
              Pending:{' '}
              <strong>
                {welcomePending === null ? '…' : welcomePending}
              </strong>
            </span>
            <button
              onClick={handleSendWelcome}
              disabled={welcomeSending || welcomePending === 0}
              className="bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {welcomeSending ? 'Sending…' : 'Send Welcome to All'}
            </button>
          </div>
          {testRecipients.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap border-t border-coffee/10 pt-3">
              <span className="text-sm text-coffee/60 font-medium">Test send</span>
              <select
                value={welcomeTestUserId}
                onChange={e => setWelcomeTestUserId(e.target.value)}
                className="border border-coffee/30 rounded-lg px-3 py-1.5 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
              >
                <option value="">— select user —</option>
                {testRecipients.map(u => (
                  <option key={u._id} value={u._id}>{u.name} ({u.email})</option>
                ))}
              </select>
              <button
                onClick={handleSendWelcomeToOne}
                disabled={welcomeTestSending || !welcomeTestUserId}
                className="bg-terracotta text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {welcomeTestSending ? 'Sending…' : 'Send'}
              </button>
            </div>
          )}
          {welcomeMsg && (
            <p className="text-sm text-coffee mt-3 font-medium">{welcomeMsg}</p>
          )}
        </div>

        {/* ── Section 3: Suggestion Pair Emails ────────────────────────── */}
        <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm mb-6">
          <h2 className="text-xl font-bold text-coffee mb-2">Suggestion Pair Emails</h2>
          <p className="text-coffee opacity-70 text-sm mb-4">
            Run the mutual-suggestion job to send emails to matched pairs, or test-send to a single user.
          </p>

          {/* Run Job Now */}
          <div className="flex items-center gap-4 flex-wrap mb-3">
            <button
              onClick={handleRunSuggestionJob}
              disabled={jobRunning}
              className="bg-terracotta text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {jobRunning ? 'Running…' : 'Run Job Now'}
            </button>
            {jobResult && (
              <span className="text-sm text-coffee font-medium">
                {jobResult.errors.length > 0
                  ? `⚠️ ${jobResult.errors[0].message}`
                  : `${jobResult.emailsSent} sent · ${jobResult.pairsSkipped} skipped`}
              </span>
            )}
          </div>

          {/* Single-recipient send */}
          {testRecipients.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap border-t border-coffee/10 pt-3">
              <span className="text-sm text-coffee/60 font-medium">Send to single user (bypasses throttle)</span>
              <select
                value={singleSuggestionUserId}
                onChange={e => { setSingleSuggestionUserId(e.target.value); setSingleSuggestionResult(null); }}
                className="border border-coffee/30 rounded-lg px-3 py-1.5 text-sm text-coffee bg-paper focus:outline-none focus:border-terracotta"
              >
                <option value="">— select user —</option>
                {testRecipients.map(u => (
                  <option key={u._id} value={u._id}>{u.name} ({u.email})</option>
                ))}
              </select>
              <button
                onClick={handleSendSuggestionToOne}
                disabled={singleSuggestionSending || !singleSuggestionUserId}
                className="bg-terracotta text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-[#d89050] transition-all border-2 border-coffee shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {singleSuggestionSending ? 'Sending…' : 'Send ▶'}
              </button>
              {singleSuggestionResult && (
                <span className="text-sm text-coffee font-medium">{singleSuggestionResult}</span>
              )}
            </div>
          )}
        </div>

        {/* ── Section 4: Broadcast History ──────────────────────────────── */}
        <div>
          <h2 className="text-xl font-bold text-coffee mb-4">Broadcast History</h2>

          {historyLoading && (
            <p className="text-coffee opacity-60 text-sm">Loading…</p>
          )}

          {historyError && (
            <p className="text-sm text-red-600">{historyError}</p>
          )}

          {!historyLoading && !historyError && broadcasts.length === 0 && (
            <p className="text-coffee opacity-60 text-sm">No broadcasts yet.</p>
          )}

          <div className="space-y-4">
            {broadcasts.map(b => (
              <div
                key={b._id}
                className="bg-cream border-2 border-coffee rounded-xl p-5 shadow-sm"
              >
                {/* Title row */}
                <div className="flex justify-between items-start gap-3 mb-2">
                  <h3 className="text-base font-bold text-coffee leading-tight">{b.title}</h3>
                  <div className="flex items-center gap-2 shrink-0">
                    {statusBadge(b.status)}
                    {(b.status === 'sent' || b.status === 'failed') && (
                      <button
                        onClick={() => handleResend(b._id)}
                        disabled={resending === b._id}
                        className="text-xs px-2 py-1 rounded border border-coffee/40 text-coffee hover:bg-terracotta hover:text-white hover:border-terracotta transition-colors disabled:opacity-50"
                      >
                        {resending === b._id ? '...' : 'Resend'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Meta */}
                <p className="text-xs text-coffee opacity-60 mb-2">
                  {b.recipientFilter === 'all_signup' ? 'All signup users' : b.recipientFilter === 'onboarded_only' ? 'Onboarded users only' : 'Single recipient'}
                  {' · '}
                  Created {new Date(b.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {b.sentAt && ` · Sent ${new Date(b.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                </p>

                {/* Sent / failed counts */}
                {(b.status === 'sent' || b.status === 'sending') && (
                  <p className="text-xs text-coffee opacity-70 mb-2">
                    📬 {b.sentCount} sent
                    {b.failedCount > 0 && ` · ${b.failedCount} failed`}
                  </p>
                )}

                {/* Feedback breakdown */}
                {b.feedbackBreakdown && b.feedbackBreakdown.length > 0 && (
                  <div className="mt-3 border-t border-coffee/20 pt-3">
                    <p className="text-xs font-semibold text-coffee mb-2">
                      Feedback ({b.totalFeedback} response{b.totalFeedback !== 1 ? 's' : ''})
                    </p>
                    <div className="space-y-1">
                      {b.feedbackBreakdown.map(fb => (
                        <div key={fb.value} className="flex items-center gap-2 text-xs text-coffee">
                          <span className="w-28 truncate">{fb.label}</span>
                          <span className="font-semibold">{fb.count}</span>
                          {fb.pct !== null && (
                            <span className="opacity-60">({fb.pct}%)</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBroadcastPage;
